import { Controller } from "@hotwired/stimulus"

// Types the assistant's live reply in (TASK-268.2, decision-78, wireframe 3.2).
//
// The job APPENDS lines to a hidden `source` element as sections close
// (Turbo Stream, at most four a second). This controller watches it with a
// MutationObserver and drains the new text into the visible `output` at
// reading pace: about 50 characters a second, faster when more than ~200 are
// queued so the screen never lags the server by more than a sentence. Each
// line (the text up to a newline) gets a green tick. A blinking caret follows
// the typing; once the queue is empty it is hidden and the `idle` target (the
// caret on the "Writing …" line) takes over. With prefers-reduced-motion the
// text appears at once.
//
// The controller lives on the live bubble. When the job's next state replaces
// the bubble with the stored reply, it disconnects and the loop stops: the
// typed text never has to match anything, because it is thrown away.
export default class extends Controller {
  static targets = ["source", "output", "idle"]
  static values = {
    charsPerSecond: { type: Number, default: 50 },
    catchUpAfter: { type: Number, default: 200 }
  }

  // State is set up in initialize(), not connect(): Stimulus calls the target
  // callbacks (idleTargetConnected) BEFORE connect() on the first pass.
  initialize() {
    this.queue = ""
    this.consumed = 0
    this.line = null
    this.frame = null
    this.lastTick = null
    this.carry = 0
    this.caret = document.createElement("span")
    this.caret.className = "typewriter-caret"
    this.caret.setAttribute("aria-hidden", "true")
    this.reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  }

  connect() {
    this.observer = new MutationObserver(() => this.pull())
    this.observer.observe(this.sourceTarget, { childList: true, characterData: true, subtree: true })
    this.pull()
    this.syncCaret()
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  idleTargetConnected() {
    this.syncCaret()
  }

  // New text in the source joins the queue.
  pull() {
    const text = this.sourceTarget.textContent
    if (text.length <= this.consumed) return

    this.queue += text.slice(this.consumed)
    this.consumed = text.length
    if (this.reducedMotion) {
      this.type(this.queue.length)
    } else {
      this.start()
    }
  }

  start() {
    if (this.frame) return
    this.lastTick = null
    this.frame = requestAnimationFrame((time) => this.tick(time))
  }

  tick(time) {
    this.frame = null
    const elapsed = this.lastTick === null ? 16 : time - this.lastTick
    this.lastTick = time

    // Characters owed since the last frame; the fraction is carried over.
    const behind = this.queue.length > this.catchUpAfterValue
    const rate = behind ? this.queue.length * 2 : this.charsPerSecondValue
    this.carry += (rate * elapsed) / 1000
    const count = Math.floor(this.carry)
    this.carry -= count
    this.type(count)

    if (this.queue.length > 0) {
      this.frame = requestAnimationFrame((next) => this.tick(next))
    } else {
      this.syncCaret()
    }
  }

  // Moves up to `count` characters from the queue onto the page.
  type(count) {
    let remaining = count
    while (remaining > 0 && this.queue.length > 0) {
      const char = this.queue[0]
      this.queue = this.queue.slice(1)
      remaining -= 1
      if (char === "\n") {
        this.line = null
        continue
      }
      this.ensureLine().append(char)
    }
    // Append the characters as one text run per call rather than per char:
    // adjacent text nodes are merged so the node count stays small.
    if (this.line) this.line.normalize()
    this.syncCaret()
  }

  ensureLine() {
    if (this.line) return this.line

    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    const tick = document.createElement("span")
    tick.className = "assistant-progress-icon font-bold text-green-600"
    tick.setAttribute("aria-hidden", "true")
    tick.textContent = "✓"
    this.line = document.createElement("span")
    this.line.className = "min-w-0"
    row.append(tick, this.line)
    this.outputTarget.append(row)
    return this.line
  }

  // The caret trails the typing while there is text to type, then the
  // "Writing …" line's caret takes over.
  syncCaret() {
    const typing = this.queue.length > 0
    if (typing && this.line) {
      this.line.append(this.caret)
    } else {
      this.caret.remove()
    }
    this.idleTargets.forEach((idle) => { idle.hidden = typing })
  }
}
